"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useGetFavorites = void 0;
var _auth = require("@ori/auth");
var _configurationManager = require("@ori/configuration-manager");
var _environment = require("@ori/environment");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _react = require("react");
var _api = require("../api");
var _utils = require("../utils");
const useGetFavorites = () => {
  const [initialized, setInitialized] = (0, _react.useState)(false);
  const [favoriteListId, setFavoriteListId] = (0, _react.useState)('');
  const [lists, setLists] = (0, _react.useState)([]);
  const [maximumAllowedPersonalLists, setMaximumAllowedPersonalLists] = (0, _react.useState)(0);
  const {
    customerId,
    isAnonymousUser,
    isUserVerified,
    token
  } = (0, _auth.useToken)();
  const {
    arePersonalListsEnabled,
    isLoadingFeatureToggle
  } = (0, _api.useGetFeatureToggle)();
  const {
    globalApiUrl
  } = (0, _configurationManager.useServiceUrls)(['globalApi']);
  const {
    tenant
  } = (0, _environment.useEnvironment)();
  const client = (0, _react.useMemo)(() => new _globalApiClientReact.ApiClient({
    baseUrl: globalApiUrl,
    tenant,
    token
  }), [globalApiUrl, tenant, token]);
  const requestLists = (0, _react.useCallback)(async () => {
    if (initialized) {
      return;
    }
    const {
      favoriteProductList,
      maximumAllowedPersonalLists: maximumAllowedPersonalListsFromApi,
      personalProductLists
    } = await (0, _api.getProductLists)({
      arePersonalListsEnabled,
      client,
      customerId,
      isAnonymousUser
    });
    if (!favoriteProductList) {
      return;
    }
    setLists([favoriteProductList, ...(0, _utils.sortLists)(personalProductLists)]);
    setFavoriteListId(favoriteProductList.id);
    setMaximumAllowedPersonalLists(maximumAllowedPersonalListsFromApi);
    setInitialized(true);
  }, [arePersonalListsEnabled, client, customerId, initialized, isAnonymousUser]);
  const updateList = (0, _react.useCallback)(async (productCodes, productListId) => {
    const list = lists.find(listItem => listItem.id === productListId);
    if (list) {
      const updatedProductCodes = (0, _utils.toggleProductCodesInList)(productCodes, list.productCodes);
      const updatedList = await (0, _api.updateProductList)({
        client,
        customerId,
        description: list.description,
        id: productListId,
        name: list.name,
        productCodes: updatedProductCodes,
        type: list.type
      });
      return {
        ...list,
        ...updatedList
      };
    }
    return undefined;
  }, [client, customerId, lists]);
  const toggleProductInLists = (0, _react.useCallback)(async ({
    productCode,
    productListIds = [favoriteListId]
  }) => {
    if (isAnonymousUser) {
      return;
    }
    const updatedLists = (await Promise.all(productListIds.map(async productListId => updateList([productCode], productListId)))).filter(list => list !== undefined);
    setLists(prevLists => (0, _utils.updateListsInLists)({
      updatedLists,
      lists: prevLists
    }));
  }, [favoriteListId, isAnonymousUser, updateList]);
  const toggleProductsInLists = (0, _react.useCallback)(async ({
    productCodes,
    productListIds = [favoriteListId]
  }) => {
    if (isAnonymousUser) {
      return;
    }
    const updatedLists = (await Promise.all(productListIds.map(async productListId => updateList(productCodes, productListId)))).filter(list => list !== undefined);
    setLists(prevLists => (0, _utils.updateListsInLists)({
      updatedLists,
      lists: prevLists
    }));
  }, [favoriteListId, isAnonymousUser, updateList]);
  (0, _react.useEffect)(() => {
    if (!isAnonymousUser && !initialized && !isLoadingFeatureToggle) {
      void requestLists();
    }
    if (isAnonymousUser && isUserVerified) {
      setInitialized(true);
    }
  }, [isAnonymousUser, initialized, requestLists, isUserVerified, isLoadingFeatureToggle]);
  return (0, _react.useMemo)(() => ({
    initialized,
    lists,
    maximumAllowedPersonalLists,
    requestLists,
    toggleProductInLists,
    toggleProductsInLists,
    updateLists: setLists
  }), [initialized, lists, maximumAllowedPersonalLists, requestLists, toggleProductInLists, toggleProductsInLists]);
};
exports.useGetFavorites = useGetFavorites;
